/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { Button, FormGroup } from 'react-bootstrap'
import Modal from '~/components/Modal'
import { useServiceContext } from '~/root'
import { useTranslation } from 'react-i18next'
import { useModal } from '~/contexts/ModalContext'
import { toast } from '~/services/toast'
import { getMyTasks } from '~/services/api/tasks'
import TaskCompleteModal from '~/screens/tasks/Actions/complete/TaskCompleteModal'
import { TASK_ACTIONS } from '~/constants/workflow'

interface WorkflowAuthorizationInfoModalProps {
  taskStatus: 'OPEN' | 'APPROVED' | 'REJECTED' | 'CANCELLED'
  workflowId: number
  onClose?: () => void
}

/**
 * WorkflowAuthorizationInfoModal
 *
 * Shown after a remove document workflow is created and the current user
 * is the only approver. Asks if they want to immediately complete the
 * authorization task.
 *
 * Ported from lego/page/browser/request-removal/WorkflowAuthorizationInfoModal.jsx
 */
const WorkflowAuthorizationInfoModal: React.FC<WorkflowAuthorizationInfoModalProps> = ({
  taskStatus,
  workflowId,
  onClose
}) => {
  const context = useServiceContext()
  const { t: translate } = useTranslation()
  const { showModal, hideModal } = useModal()
  const [isOpen, setIsOpen] = useState<boolean>(false)

  useEffect(() => {
    setIsOpen(true)
  }, [])

  /**
   * Handle OK button click - fetch the task and show TaskCompleteModal
   * Uses /api/tasks endpoint with userId (like legacy getMyTasks)
   */
  function ok() {
    const userId = context.service.auth.getUser()?.id

    getMyTasks({
      taskStatus: taskStatus,
      workflowId: workflowId,
      userId: userId
    }).then(
      function (data: any) {
        const elements = data?.elements ?? []
        const targetTask = elements.length > 0 ? elements[0] : null

        if (!targetTask?.id) {
          toast.error(translate('SOMETHING_WENT_WRONG_MSG_ERROR'))
          return
        }

        // Just close this modal (no success toast or page reload here)
        setIsOpen(false)

        // Show TaskCompleteModal for the authorization task
        showModal(
          <TaskCompleteModal
            task={targetTask}
            type={TASK_ACTIONS.taskComplete}
            onClose={() => {
              hideModal()
              // Trigger page refresh after task completion
              window.location.reload()
            }}
          />
        )
      },
      function (error: any) {
        toast.error(translate('SOMETHING_WENT_WRONG_MSG_ERROR'))
      }
    )
  }

  /**
   * Handle close/cancel - show success message and trigger updates
   */
  const doClose = () => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
    toast.success(translate('BROWSER_REQUEST_REMOVAL_SUCCESS_MSG'))
    // Trigger page refresh
    window.location.reload()
  }

  return (
    <>
      <Modal title={translate('CR_REQUEST_AUTHORIZATION_TITLE')} size="md" show={isOpen} handleClose={doClose}>
        <Modal.Body>
          <FormGroup className="form-group wparag">
            <label>{translate('CR_REQUEST_AUTHORIZATION_INFO_QUESTION')}</label>
          </FormGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button data-action="cancel" variant="link" onClick={doClose} id="workflow-auth-info-cancel-btn">
            {translate('CANCEL')}
          </Button>
          <Button
            data-action="save"
            className="btn btn-primary text-capitalize"
            onClick={ok}
            id="workflow-auth-info-ok-btn"
          >
            {translate('BROWSER_SEND_RQST')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default WorkflowAuthorizationInfoModal
